import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PeriodSelector } from '../components/PeriodSelector';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Upload, 
  TrendingUp,
  DollarSign,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const RevenuePage = () => {
  const { currentLocation, api } = useAuth();
  const navigate = useNavigate();
  const [revenue, setRevenue] = useState([]);
  const [datesWithOrders, setDatesWithOrders] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [periodStart, setPeriodStart] = useState(startOfMonth(new Date()));
  const [periodEnd, setPeriodEnd] = useState(endOfMonth(new Date()));
  const [periodPreset, setPeriodPreset] = useState('this_month');

  useEffect(() => {
    if (currentLocation) {
      fetchRevenue();
    }
  }, [currentLocation, periodStart, periodEnd]);

  const fetchRevenue = async () => {
    setLoading(true);
    try {
      const dateParams = {
        location_id: currentLocation.id,
        start_date: format(periodStart, 'yyyy-MM-dd'),
        end_date: format(periodEnd, 'yyyy-MM-dd'),
      };
      const [revRes, orderDatesRes] = await Promise.all([
        api.get('/revenue', { params: dateParams }),
        api.get('/orders/dates-available', { params: { location_id: currentLocation.id } }).catch(() => ({ data: [] }))
      ]);
      setRevenue(revRes.data);
      // Build a set of dates that have order data for drill-down
      setDatesWithOrders(new Set(orderDatesRes.data.map(d => d.date)));
    } catch (err) {
      console.error('Error fetching revenue:', err);
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => `£${val?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00'}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });

  // Calculate totals
  const totalRevenue = revenue.reduce((sum, r) => sum + (r.amount || 0), 0);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="revenue-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Revenue</h1>
          <p className="text-muted-foreground">Sales &amp; revenue by day — imports live in the Imports hub.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <PeriodSelector
            startDate={periodStart}
            endDate={periodEnd}
            preset={periodPreset}
            onPresetChange={setPeriodPreset}
            onChange={(s, e) => { setPeriodStart(s); setPeriodEnd(e); }}
          />
          <Button
            variant="outline"
            onClick={() => navigate('/imports?tab=sales')}
            data-testid="open-imports-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import sales
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Revenue
            </CardTitle>
            <DollarSign className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono-num text-profit" data-testid="total-revenue">
              {formatCurrency(totalRevenue)}
            </div>
            <p className="text-sm text-muted-foreground">From {revenue.length} entries</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Table */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Entries</CardTitle>
          <CardDescription>All recorded revenue from your POS and manual entries</CardDescription>
        </CardHeader>
        <CardContent>
          {revenue.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No revenue data yet</h3>
              <p className="text-muted-foreground mb-4">
                Import your first sales report from the Imports hub.
              </p>
              <Button onClick={() => navigate('/imports?tab=sales')} data-testid="empty-open-imports-btn">
                <Upload className="w-4 h-4 mr-2" />
                Go to Imports
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Orders / Guests</TableHead>
                    <TableHead className="text-right">Net Sales</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revenue.slice(0, 50).map((rev) => {
                    const hasOrderData = datesWithOrders.has(rev.date);
                    return (
                      <TableRow 
                        key={rev.id} 
                        className={`table-row-hover ${hasOrderData ? 'cursor-pointer' : ''}`}
                        onClick={() => hasOrderData && navigate(`/front-of-house?date=${rev.date}`)}
                      >
                        <TableCell className="font-medium">
                          {formatDate(rev.date)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{rev.category === 'daily_sales' ? 'Daily Total' : rev.category}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {rev.total_orders ? (
                            <span>{rev.total_orders} orders • {rev.total_guests || 0} guests</span>
                          ) : (
                            rev.menu_item || '-'
                          )}
                        </TableCell>
                        <TableCell className="text-right mono-num font-medium text-profit">
                          {formatCurrency(rev.amount)}
                        </TableCell>
                        <TableCell>
                          {hasOrderData && (
                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {revenue.length > 50 && (
            <p className="text-sm text-muted-foreground text-center mt-4">
              Showing 50 of {revenue.length} entries
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RevenuePage;
